import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { nonProspectDomainsApi, previewNonProspectDomains } from '@/api/nonProspectDomains'
import { useToast } from '@/hooks/useToast'
import { nonProspectScopeKey, type NonProspectDomainsViewDto, type NonProspectDomainsScope } from '@/types/nonProspectDomains'
import type { SaveNonProspectDomains } from '@/components/nonProspectDomains/useNonProspectDomainsEditor'
export type { NonProspectDomainsScope } from '@/types/nonProspectDomains'

export function useNonProspectDomains(scope: NonProspectDomainsScope) {
  const { showSuccess } = useToast()
  const [view, setView] = useState<NonProspectDomainsViewDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [version, setVersion] = useState(0)
  const generation = useRef(0)
  const current = useRef<NonProspectDomainsViewDto | null>(null)
  const busy = useRef(false)
  const scopeKey = nonProspectScopeKey(scope)
  useEffect(() => {
    const mine = ++generation.current
    current.current = null; setView(null); setLoading(true); setError(null)
    const run = async () => {
      try {
        const response = scope.level === 'company'
          ? await nonProspectDomainsApi.getForCompany(scope.clientCompanyId)
          : await nonProspectDomainsApi.getForWorkspace(scope.clientCompanyId, scope.workspaceId)
        if (mine !== generation.current) return
        if (response.data && !response.error) { current.current = response.data; setView(response.data) }
        else setError('Unable to load non-prospect domains. Try again.')
      } catch { if (mine === generation.current) setError('Unable to load non-prospect domains. Try again.') }
      finally { if (mine === generation.current) setLoading(false) }
    }
    void run()
    return () => { generation.current++; current.current = null }
  }, [scopeKey, version])
  const save: SaveNonProspectDomains = async (domains, expectedETag) => {
    if (!current.current || busy.current) return null
    if (expectedETag && expectedETag !== current.current.ETag) return { status: 412, statusText: 'Precondition Failed', error: 'This list changed. Reload and try again.' }
    busy.current = true
    const mine = generation.current
    const dto = { ETag: expectedETag ?? current.current.ETag, NonProspectDomains: domains }
    try {
      const response = scope.level === 'company'
        ? await nonProspectDomainsApi.patchForCompany(scope.clientCompanyId, dto)
        : await nonProspectDomainsApi.patchForWorkspace(scope.clientCompanyId, scope.workspaceId, dto)
      if (mine !== generation.current) return null
      if (response.data && !response.error && response.status < 400) {
        current.current = response.data; setView(response.data)
        showSuccess('Non-prospect domains saved', 'Audience views will update on the next refresh.')
      }
      return response
    } finally { busy.current = false }
  }
  const reload = useCallback(() => { if (!busy.current) setVersion(value => value + 1) }, [])
  return { view, loading, error, save, reload, preview: (file: File, column?: number) => previewNonProspectDomains(scope, file, column) }
}
